import { useEffect, useState, type PointerEvent } from 'react';
import type { InlineTextStyle } from '@/components/canvas/elements/text/model/textDocument';

// ── Preset palettes ──────────────────────────────────────────────────────────

const TEXT_COLORS = [
  '#000000', '#374151', '#6b7280', '#9ca3af',
  '#ef4444', '#f97316', '#eab308', '#22c55e',
  '#06b6d4', '#3b82f6', '#6366f1', '#a855f7',
  '#ec4899',
];

const BG_COLORS = [
  '#fef9c3', '#ffedd5', '#fce7f3', '#ede9fe',
  '#dcfce7', '#cffafe', '#dbeafe', '#fee2e2',
];

const FONT_SIZES = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48];

// ── Types ────────────────────────────────────────────────────────────────────

export interface InlineToolbarProps {
  selectionRect: DOMRect | null;
  /** Active styles across the current selection — used for toggle rendering. */
  activeStyles: InlineTextStyle;
  onInlineStyleCommand: (style: InlineTextStyle) => void;
  onInteractionStart?: () => void;
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
}

// ── Component ────────────────────────────────────────────────────────────────

export function InlineToolbar({
  selectionRect,
  activeStyles = {},
  onInlineStyleCommand,
  onInteractionStart,
  onHoverStart,
  onHoverEnd,
}: InlineToolbarProps) {
  const [openPicker, setOpenPicker] = useState<'color' | 'bg' | 'size' | null>(null);

  // Close any open picker whenever the toolbar repositions (new selection).
  useEffect(() => {
    setOpenPicker(null);
  }, [selectionRect]);

  if (!selectionRect) return null;

  // ── Event helpers ──────────────────────────────────────────────────────────

  /** Execute a style command, preserving the text selection. */
  const exec = (event: PointerEvent<HTMLElement>, style: InlineTextStyle) => {
    onInteractionStart?.();
    event.preventDefault();
    event.stopPropagation();
    onInlineStyleCommand(style);
  };

  /**
   * Toggle a boolean style: if ALL selected runs already carry it, remove it;
   * otherwise apply it.  Removal is done by passing `false` — the normaliser
   * strips falsy boolean properties so runs are cleanly merged afterwards.
   */
  const toggle = (event: PointerEvent<HTMLElement>, key: keyof InlineTextStyle) => {
    const isActive = !!(activeStyles as Record<string, unknown>)[key];
    exec(event, { [key]: isActive ? false : true } as InlineTextStyle);
  };

  /** Open / close one of the pickers without losing the text selection. */
  const openPickerHandler = (
    event: PointerEvent<HTMLElement>,
    picker: 'color' | 'bg' | 'size',
  ) => {
    onInteractionStart?.();
    event.preventDefault();
    event.stopPropagation();
    setOpenPicker((prev) => (prev === picker ? null : picker));
  };

  /** Prevent the toolbar container from stealing focus / collapsing selection. */
  const guardPointer = (event: PointerEvent<HTMLElement>) => {
    onInteractionStart?.();
    event.preventDefault();
  };

  // ── Computed positioning ───────────────────────────────────────────────────

  // Height of whichever picker panel is currently open (0 when closed).
  const pickerHeight = openPicker === 'size' ? 88 : openPicker ? 40 : 0;
  const top = Math.max(8, selectionRect.top - 42 - pickerHeight);
  const left = Math.max(4, selectionRect.left);

  // ── Render helpers ─────────────────────────────────────────────────────────

  const btnCls = (active: boolean) =>
    `rounded px-2 py-1 select-none text-xs leading-none ${
      active ? 'bg-primary/15 text-primary font-semibold' : 'hover:bg-default-100'
    }`;

  const divider = <div className="mx-0.5 h-3.5 w-px bg-divider self-center" />;

  return (
    <div
      data-text-edit-chrome
      className="fixed z-[100] rounded-lg border border-divider bg-surface/95 shadow-lg backdrop-blur overflow-hidden"
      style={{ left, top }}
      onPointerEnter={onHoverStart}
      onPointerLeave={onHoverEnd}
      onPointerDownCapture={guardPointer}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* ── Picker panels (rendered above the main bar) ── */}

      {openPicker === 'color' && (
        <div className="flex items-center gap-1 border-b border-divider px-2 py-1.5">
          {/* "No color" swatch */}
          <button
            type="button"
            tabIndex={-1}
            className="h-5 w-5 rounded-full border border-dashed border-divider hover:scale-110 transition-transform"
            title="默认颜色"
            onPointerDown={(e) => {
              exec(e, { color: undefined });
              setOpenPicker(null);
            }}
          />
          {TEXT_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              tabIndex={-1}
              className={`h-5 w-5 rounded-full transition-transform hover:scale-110 ${
                activeStyles.color === color ? 'ring-2 ring-primary ring-offset-1' : ''
              }`}
              style={{ background: color }}
              title={color}
              onPointerDown={(e) => {
                exec(e, { color: activeStyles.color === color ? undefined : color });
                setOpenPicker(null);
              }}
            />
          ))}
        </div>
      )}

      {openPicker === 'bg' && (
        <div className="flex items-center gap-1 border-b border-divider px-2 py-1.5">
          {/* "No background" swatch */}
          <button
            type="button"
            tabIndex={-1}
            className="h-5 w-5 rounded border border-dashed border-divider hover:scale-110 transition-transform"
            title="无背景色"
            onPointerDown={(e) => {
              exec(e, { backgroundColor: undefined });
              setOpenPicker(null);
            }}
          />
          {BG_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              tabIndex={-1}
              className={`h-5 w-5 rounded border border-divider/50 transition-transform hover:scale-110 ${
                activeStyles.backgroundColor === color ? 'ring-2 ring-primary ring-offset-1' : ''
              }`}
              style={{ background: color }}
              title={color}
              onPointerDown={(e) => {
                exec(e, {
                  backgroundColor:
                    activeStyles.backgroundColor === color ? undefined : color,
                });
                setOpenPicker(null);
              }}
            />
          ))}
        </div>
      )}

      {openPicker === 'size' && (
        <div className="border-b border-divider p-1.5 grid grid-cols-4 gap-0.5">
          <button
            type="button"
            tabIndex={-1}
            className={`rounded px-1 py-0.5 text-xs text-center hover:bg-default-100 select-none ${
              !activeStyles.fontSize ? 'bg-primary/15 text-primary' : ''
            }`}
            onPointerDown={(e) => {
              exec(e, { fontSize: undefined });
              setOpenPicker(null);
            }}
          >
            默认
          </button>
          {FONT_SIZES.map((size) => (
            <button
              key={size}
              type="button"
              tabIndex={-1}
              className={`rounded px-1 py-0.5 text-xs text-center hover:bg-default-100 select-none ${
                activeStyles.fontSize === size ? 'bg-primary/15 text-primary' : ''
              }`}
              onPointerDown={(e) => {
                exec(e, {
                  fontSize: activeStyles.fontSize === size ? undefined : size,
                });
                setOpenPicker(null);
              }}
            >
              {size}
            </button>
          ))}
        </div>
      )}

      {/* ── Main toolbar row ── */}
      <div className="flex items-center gap-0.5 px-1.5 py-1 text-xs text-foreground">
        {/* Bold */}
        <button
          type="button"
          tabIndex={-1}
          className={btnCls(!!activeStyles.bold)}
          title="加粗 (Bold)"
          onPointerDown={(e) => toggle(e, 'bold')}
        >
          <span className="font-bold">B</span>
        </button>

        {/* Italic */}
        <button
          type="button"
          tabIndex={-1}
          className={btnCls(!!activeStyles.italic)}
          title="斜体 (Italic)"
          onPointerDown={(e) => toggle(e, 'italic')}
        >
          <span className="italic font-serif">I</span>
        </button>

        {/* Underline */}
        <button
          type="button"
          tabIndex={-1}
          className={btnCls(!!activeStyles.underline)}
          title="下划线 (Underline)"
          onPointerDown={(e) => toggle(e, 'underline')}
        >
          <span className="underline">U</span>
        </button>

        {/* Strikethrough */}
        <button
          type="button"
          tabIndex={-1}
          className={btnCls(!!activeStyles.strike)}
          title="删除线 (Strikethrough)"
          onPointerDown={(e) => toggle(e, 'strike')}
        >
          <span className="line-through">S</span>
        </button>

        {divider}

        {/* Superscript */}
        <button
          type="button"
          tabIndex={-1}
          className={btnCls(!!activeStyles.superscript)}
          title="上标 (Superscript)"
          onPointerDown={(e) => toggle(e, 'superscript')}
        >
          X<sup className="text-[0.65em]">2</sup>
        </button>

        {/* Subscript */}
        <button
          type="button"
          tabIndex={-1}
          className={btnCls(!!activeStyles.subscript)}
          title="下标 (Subscript)"
          onPointerDown={(e) => toggle(e, 'subscript')}
        >
          X<sub className="text-[0.65em]">2</sub>
        </button>

        {divider}

        {/* Text color button — shows active color as an underbar */}
        <button
          type="button"
          tabIndex={-1}
          className={`relative rounded px-2 py-0.5 pb-1.5 select-none ${
            openPicker === 'color' ? 'bg-primary/15' : 'hover:bg-default-100'
          }`}
          title="文字颜色"
          onPointerDown={(e) => openPickerHandler(e, 'color')}
        >
          <span className="font-bold text-xs leading-none">A</span>
          <span
            className="absolute bottom-0.5 left-1 right-1 h-[3px] rounded-full"
            style={{ background: activeStyles.color ?? '#000000' }}
          />
        </button>

        {/* Background highlight button — shows active bg color as underbar */}
        <button
          type="button"
          tabIndex={-1}
          className={`relative rounded px-2 py-0.5 pb-1.5 select-none ${
            openPicker === 'bg' ? 'bg-primary/15' : 'hover:bg-default-100'
          }`}
          title="背景颜色 / 高亮"
          onPointerDown={(e) => openPickerHandler(e, 'bg')}
        >
          <span className="text-xs leading-none">H</span>
          <span
            className="absolute bottom-0.5 left-1 right-1 h-[3px] rounded-full border border-divider/40"
            style={{ background: activeStyles.backgroundColor ?? 'transparent' }}
          />
        </button>

        {divider}

        {/* Font size */}
        <button
          type="button"
          tabIndex={-1}
          className={`rounded px-2 py-1 select-none min-w-[2.5rem] text-center ${
            openPicker === 'size' ? 'bg-primary/15 text-primary' : 'hover:bg-default-100'
          }`}
          title="字号"
          onPointerDown={(e) => openPickerHandler(e, 'size')}
        >
          {activeStyles.fontSize ?? '默'}
        </button>

        {divider}

        {/* Clear all inline formatting */}
        <button
          type="button"
          tabIndex={-1}
          className="rounded px-2 py-1 hover:bg-default-100 select-none text-foreground/50"
          title="清除格式"
          onPointerDown={(e) =>
            exec(e, {
              bold: false,
              italic: false,
              underline: false,
              strike: false,
              superscript: false,
              subscript: false,
              color: undefined,
              backgroundColor: undefined,
              fontSize: undefined,
            })
          }
        >
          ✕
        </button>
      </div>
    </div>
  );
}
